<template>
  <v-dialog v-model="dialogModel" max-width="960px">
    <v-card>
      <v-card-title>{{ $t("employeeHolidays.request.title") }}</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="handleFormSubmit">
          <v-container>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  :label="$t('employeeHolidays.request.type')"
                  :items="holidayTypeOptions"
                  item-title="title"
                  item-value="value"
                  v-model="requestForm.holiday_type_id"
                  outlined
                  :rules="[requiredTypeRule]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="requestRangeMenuModel"
                  :close-on-content-click="false"
                  max-width="360"
                  transition="scale-transition"
                >
                  <template #activator="{ props: menuProps }">
                    <div class="d-flex ga-2 flex-wrap w-100" v-bind="menuProps">
                      <v-text-field
                        class="flex-grow-1"
                        :label="$t('employeeHolidays.request.start')"
                        outlined
                        readonly
                        :model-value="requestForm.start_date"
                        @click.stop="onRangeControlClick('start', $event, menuProps)"
                        @mousedown="onRangePointerDown('start')"
                        @focus="onRangeFieldFocus('start')"
                        @keydown.enter.prevent="onRangeKeyboardOpen('start')"
                        @keydown.space.prevent="onRangeKeyboardOpen('start')"
                        @click:control.stop="onRangeControlClick('start', $event, menuProps)"
                      ></v-text-field>
                      <v-text-field
                        class="flex-grow-1"
                        :label="$t('employeeHolidays.request.end')"
                        outlined
                        readonly
                        :model-value="requestForm.end_date"
                        @click.stop="onRangeControlClick('end', $event, menuProps)"
                        @mousedown="onRangePointerDown('end')"
                        @focus="onRangeFieldFocus('end')"
                        @keydown.enter.prevent="onRangeKeyboardOpen('end')"
                        @keydown.space.prevent="onRangeKeyboardOpen('end')"
                        @click:control.stop="onRangeControlClick('end', $event, menuProps)"
                      ></v-text-field>
                    </div>
                  </template>
                  <v-card>
                    <v-date-picker
                      :model-value="rangeDateModel"
                      color="primary"
                      range
                      :allowed-dates="allowedCalendarDates"
                      :events="calendarEvents"
                      :first-day-of-week="1"
                      show-adjacent-months
                      @update:model-value="onCalendarRangeChange"
                    ></v-date-picker>
                    <v-card-actions class="justify-end">
                      <v-btn variant="text" @click="requestRangeMenuModel = false">{{ $t("app.actions.cancel") }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  :label="$t('employeeHolidays.request.days')"
                  type="number"
                  v-model="requestForm.working_days_requested"
                  outlined
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="!hideStatus">
                <v-select
                  :label="$t('employeeHolidays.request.status')"
                  :items="statusOptions"
                  v-model="requestForm.status"
                  outlined
                  @update:model-value="onStatusChange"
                ></v-select>
              </v-col>
              <v-col cols="12" md="4" v-if="!hideApproved">
                <v-switch
                  :label="$t('employeeHolidays.request.approved')"
                  v-model="requestForm.approved"
                  inset
                  @update:model-value="onApprovedChange"
                ></v-switch>
              </v-col>
              <v-col cols="12">
                <v-textarea :label="$t('employeeHolidays.request.notes')" v-model="requestForm.notes" rows="3" outlined></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="showCancelAction"
          variant="text"
          color="error"
          :loading="cancelActionLoading"
          @click="emitCancelRequest"
        >
          {{ $t("employeeHolidays.request.cancelAction") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="handleDialogCancel">{{ $t("app.actions.cancel") }}</v-btn>
        <v-btn color="primary" :loading="loading" @click="handleFormSubmit">
          {{ $t("app.actions.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, defineComponent, toRefs } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "HolidayRequestPopUp",
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    requestForm: {
      type: Object,
      required: true,
    },
    holidayTypeOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    requestRangeMenu: {
      type: Boolean,
      default: false,
    },
    rangeDateModel: {
      type: [Array, Object],
      default: () => [],
    },
    allowedCalendarDates: {
      type: Function,
      required: true,
    },
    calendarEvents: {
      type: Array,
      default: () => [],
    },
    onCalendarRangeChange: {
      type: Function,
      required: true,
    },
    onRangePointerDown: {
      type: Function,
      required: true,
    },
    onRangeFieldFocus: {
      type: Function,
      required: true,
    },
    onRangeKeyboardOpen: {
      type: Function,
      required: true,
    },
    onRangeControlClick: {
      type: Function,
      required: true,
    },
    hideStatus: {
      type: Boolean,
      default: false,
    },
    hideApproved: {
      type: Boolean,
      default: false,
    },
    showCancelAction: {
      type: Boolean,
      default: false,
    },
    cancelActionLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update:requestRangeMenu", "submit", "cancel", "cancel-request"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const dialogModel = computed({
      get: () => props.modelValue,
      set: (value) => emit("update:modelValue", value),
    });

    const requestRangeMenuModel = computed({
      get: () => props.requestRangeMenu,
      set: (value) => emit("update:requestRangeMenu", value),
    });

    const handleDialogCancel = () => {
      emit("cancel");
      emit("update:modelValue", false);
    };

    const handleFormSubmit = () => {
      emit("submit");
    };

    const emitCancelRequest = () => {
      emit("cancel-request");
    };

    const requiredTypeRule = (val) => !!val || t("employeeHolidays.request.requiredType");

    const {
      requestForm,
      holidayTypeOptions,
      statusOptions,
      rangeDateModel,
      allowedCalendarDates,
      calendarEvents,
      onCalendarRangeChange,
      onRangePointerDown,
      onRangeFieldFocus,
      onRangeKeyboardOpen,
      onRangeControlClick,
      loading,
      hideStatus,
      hideApproved,
      showCancelAction,
      cancelActionLoading,
    } = toRefs(props);

    const onApprovedChange = (value) => {
      if (value && props.requestForm.status !== "approved") {
        props.requestForm.status = "approved";
      }
    };

    const onStatusChange = (value) => {
      if (value === "approved" && !props.requestForm.approved) {
        props.requestForm.approved = true;
      } else if (value !== "approved" && props.requestForm.approved) {
        props.requestForm.approved = false;
      }
    };

    return {
      dialogModel,
      requestRangeMenuModel,
      handleDialogCancel,
      handleFormSubmit,
      onApprovedChange,
      onStatusChange,
      emitCancelRequest,
      requiredTypeRule,
      requestForm,
      holidayTypeOptions,
      statusOptions,
      rangeDateModel,
      allowedCalendarDates,
      calendarEvents,
      onCalendarRangeChange,
      onRangePointerDown,
      onRangeFieldFocus,
      onRangeKeyboardOpen,
      onRangeControlClick,
      loading,
      hideStatus,
      hideApproved,
      showCancelAction,
      cancelActionLoading,
    };
  },
});
</script>
